import { useEffect, useState } from 'react'
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material'
import { EMAIL, enviarRelatorio, lerConfigRelatorio, lerEmails, listarEnvios, tituloResumo, type EnvioRelatorio, type ResumoSemanal } from '../api/resumos'

const quando = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' })

/** Manda um resumo por e-mail: começa com os destinatários da marca, dá para mudar só neste envio. */
export function EnviarRelatorioDialog({ r, onClose }: { r: ResumoSemanal; onClose: () => void }) {
  const [texto, setTexto] = useState('')
  const [envios, setEnvios] = useState<EnvioRelatorio[]>([])
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  useEffect(() => {
    lerConfigRelatorio(r.marcaId)
      .then((c) => setTexto(c.destinatarios.join(', ')))
      .catch((e: Error) => setErro(e.message))
    listarEnvios(r.id).then(setEnvios).catch(() => setEnvios([]))
  }, [r.id, r.marcaId])

  const para = lerEmails(texto)
  const invalidos = para.filter((e) => !EMAIL.test(e))

  const enviar = async () => {
    setEnviando(true)
    setErro(null)
    setOk(null)
    try {
      const res = await enviarRelatorio(r.id, para)
      setOk(`Enviado para ${res.para.join(', ')}.`)
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setEnviando(false)
      listarEnvios(r.id).then(setEnvios).catch(() => undefined)
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Enviar por e-mail</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {r.marca} · {tituloResumo(r)}. Vai como e-mail formatado, com os indicadores, o texto e o diagnóstico.
          </Typography>
          <TextField
            label="Para"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            multiline
            minRows={2}
            error={invalidos.length > 0}
            helperText={
              invalidos.length
                ? `E-mail inválido: ${invalidos.join(', ')}`
                : 'Separe por vírgula. Começa com os destinatários da marca (cadastrados nesta página); a mudança vale só para este envio.'
            }
          />
          {erro ? <Alert severity="error">{erro}</Alert> : null}
          {ok ? <Alert severity="success">{ok}</Alert> : null}
          {envios.length ? (
            <Stack spacing={0.75}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                Envios deste resumo
              </Typography>
              {envios.map((e) => (
                <Stack key={e.id} direction="row" spacing={1} alignItems="flex-start">
                  <Chip size="small" variant="outlined" color={e.status === 'enviado' ? 'success' : 'error'} label={e.status === 'enviado' ? 'Enviado' : 'Erro'} />
                  <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {quando.format(new Date(e.enviadoEm))} · {e.origem === 'agendada' ? 'automático' : 'manual'} · {e.para.join(', ')}
                    {e.mensagem ? (
                      <Typography component="span" variant="body2" color="error.main">
                        {' '}
                        — {e.mensagem}
                      </Typography>
                    ) : null}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Fechar
        </Button>
        <Button variant="contained" disabled={enviando || !para.length || invalidos.length > 0} onClick={enviar} sx={{ textTransform: 'none' }}>
          {enviando ? 'Enviando…' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
